import React from "react";
import { createPortal } from "react-dom";
import { Trash2, TriangleAlert, LoaderCircle, X } from "lucide-react";
import "./DashboardDossierOverviewCard.css";

function formatEuro(value) {
  if (value == null || value === "") return "—";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "—";
  return `€ ${numeric.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDossierStatus(status, t) {
  return (
    {
      DRAFT: t("dossierStatus.draft", "Draft"),
      READY_FOR_EXPORT: t("dossierStatus.readyForExport", "Ready for export"),
      EXPORTED: t("dossierStatus.exported", "Exported"),
    }[status] ||
    status ||
    "—"
  );
}

export default function DashboardDossierOverviewCard({
  summary = {},
  onUpdate,
  onDownload,
  isDownloading = false,
  onDelete,
  isDeleting = false,
  t,
}) {
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState("");
  const cancelButtonRef = React.useRef(null);

  // The backend is authoritative. The GET /api/homeowner/me/ summary must
  // expose can_delete_dossier; do not infer eligibility from lender_name.
  const canDelete =
    summary?.dossier_status === "READY_FOR_EXPORT" &&
    summary?.can_delete_dossier === true &&
    typeof onDelete === "function";

  const closeDeleteModal = React.useCallback(() => {
    if (isDeleting) return;
    setDeleteModalOpen(false);
    setDeleteError("");
  }, [isDeleting]);

  React.useEffect(() => {
    if (!deleteModalOpen) return undefined;
    cancelButtonRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !isDeleting) {
        setDeleteModalOpen(false);
        setDeleteError("");
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [deleteModalOpen, isDeleting]);

  React.useEffect(() => {
    if (!canDelete && !isDeleting) {
      setDeleteModalOpen(false);
    }
  }, [canDelete, isDeleting]);

  async function handleConfirmDelete() {
    if (!canDelete || isDeleting) return;
    setDeleteError("");
    try {
      await onDelete();
      setDeleteModalOpen(false);
    } catch (error) {
      setDeleteError(
        error?.message ||
          t(
            "cards.completedDossier.delete.error",
            "Could not delete the dossier.",
          ),
      );
    }
  }

  return (
    <>
      <div className="hb-dossier-overview-card">
        <div className="hb-dossier-overview-card__header">
          <div>
            <p className="hb-dossier-overview-card__eyebrow">
              {t("cards.completedDossier.eyebrow", "Dossier ready")}
            </p>
            <h3>
              {t("cards.completedDossier.title", "Your dossier is complete")}
            </h3>
          </div>
          <span className="hb-dossier-overview-card__pill">
            {formatDossierStatus(summary?.dossier_status, t)}
          </span>
        </div>

        <p className="hb-dossier-overview-card__description">
          {t(
            "cards.completedDossier.description",
            "Your loan preparation dossier has been completed. You can download the full package or update your information before sharing it with a lender.",
          )}
        </p>

        <div className="hb-dossier-overview-card__stats">
          <div className="hb-stat-box">
            <span>{t("cards.completedDossier.reference", "Reference")}</span>
            <strong>{summary?.dossier_code || "—"}</strong>
            <small>
              {t("cards.completedDossier.referenceHint", "Dossier ID")}
            </small>
          </div>
          <div className="hb-stat-box">
            <span>{t("cards.completedDossier.lender", "Selected lender")}</span>
            <strong>{summary?.lender_name || "—"}</strong>
            <small>
              {t("cards.completedDossier.lenderHint", "Assigned partner")}
            </small>
          </div>
          <div className="hb-stat-box">
            <span>{t("cards.completedDossier.totalLoan", "Total loan")}</span>
            <strong>{formatEuro(summary?.total_loan_amount)}</strong>
            <small>
              {t(
                "cards.completedDossier.totalLoanHint",
                "Purchase + renovation",
              )}
            </small>
          </div>
          <div className="hb-stat-box">
            <span>
              {t("cards.completedDossier.renovationLoan", "Renovation loan")}
            </span>
            <strong>{formatEuro(summary?.renovation_loan_amount)}</strong>
            <small>
              {t("cards.completedDossier.renovationHint", "Energy upgrades")}
            </small>
          </div>
        </div>

        <div className="hb-dossier-overview-card__meta">
          <div>
            <span>
              {t("cards.completedDossier.statusLabel", "Dossier status")}
            </span>
            <strong>{formatDossierStatus(summary?.dossier_status, t)}</strong>
          </div>
          <div>
            <span>{t("cards.completedDossier.lastUpdate", "Last update")}</span>
            <strong>{formatDateTime(summary?.last_update)}</strong>
          </div>
        </div>

        <div className="hb-dossier-overview-card__actions">
          <button
            type="button"
            className="hb-dossier-overview-card__btn hb-dossier-overview-card__btn--ghost"
            onClick={onUpdate}
            disabled={isDeleting}
          >
            {t("buttons.update", "Update")}
          </button>

          <button
            type="button"
            className="hb-dossier-overview-card__btn hb-dossier-overview-card__btn--primary"
            onClick={onDownload}
            disabled={isDownloading || isDeleting}
          >
            {isDownloading
              ? t("buttons.downloading", "Downloading...")
              : t("buttons.download", "Download dossier")}
          </button>
        </div>

        {canDelete && (
          <div className="hb-dossier-overview-card__delete-row">
            <button
              type="button"
              className="hb-dossier-overview-card__delete-button"
              onClick={() => {
                setDeleteError("");
                setDeleteModalOpen(true);
              }}
              disabled={isDeleting || isDownloading}
            >
              <Trash2 size={16} />
              {t("cards.completedDossier.delete.button", "Delete dossier")}
            </button>
          </div>
        )}
      </div>

      {deleteModalOpen &&
        canDelete &&
        createPortal(
          <div
            className="hb-overview-delete-backdrop"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) closeDeleteModal();
            }}
          >
            <div
              className="hb-overview-delete-modal"
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="hb-overview-delete-title"
              aria-describedby="hb-overview-delete-description"
            >
              <div className="hb-overview-delete-modal__header">
                <span
                  className="hb-overview-delete-modal__icon"
                  aria-hidden="true"
                >
                  <TriangleAlert size={24} />
                </span>
                <button
                  className="hb-overview-delete-modal__close"
                  type="button"
                  aria-label={t("cards.completedDossier.delete.close", "Close")}
                  onClick={closeDeleteModal}
                  disabled={isDeleting}
                >
                  <X size={20} />
                </button>
              </div>
              <h3 id="hb-overview-delete-title">
                {t(
                  "cards.completedDossier.delete.title",
                  "Delete this dossier?",
                )}
              </h3>
              <p id="hb-overview-delete-description">
                {t(
                  "cards.completedDossier.delete.description",
                  "This dossier is ready for export and has not been linked to a lender loan case. Deleting it is permanent and cannot be undone.",
                )}
              </p>
              {summary?.dossier_code && (
                <p className="hb-overview-delete-modal__reference">
                  {t("cards.completedDossier.reference", "Reference")}:{" "}
                  <strong>{summary.dossier_code}</strong>
                </p>
              )}
              {deleteError && (
                <div className="hb-overview-delete-modal__error" role="alert">
                  {deleteError}
                </div>
              )}
              <div className="hb-overview-delete-modal__actions">
                <button
                  ref={cancelButtonRef}
                  type="button"
                  className="hb-overview-delete-modal__cancel"
                  onClick={closeDeleteModal}
                  disabled={isDeleting}
                >
                  {t("cards.completedDossier.delete.cancel", "Cancel")}
                </button>
                <button
                  type="button"
                  className="hb-overview-delete-modal__confirm"
                  onClick={handleConfirmDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <LoaderCircle
                        className="hb-overview-delete-modal__spinner"
                        size={17}
                      />
                      {t(
                        "cards.completedDossier.delete.deleting",
                        "Deleting...",
                      )}
                    </>
                  ) : (
                    <>
                      <Trash2 size={17} />
                      {t(
                        "cards.completedDossier.delete.confirm",
                        "Delete dossier",
                      )}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
